import { z } from 'zod';

// Schemas for the ai_channel_snapshot table and its associated NOTIFY payload.
// The snapshot table drives the chat-source AI cadence: one row per channel window,
// worked by the bot's snapshot worker rather than the per-record enrichment listener.

// --- Enums ------------------------------------------------------------------

export const AiChannelSnapshotStatusSchema = z.enum(['pending', 'claimed', 'done', 'dead']);
export type AiChannelSnapshotStatus = z.infer<typeof AiChannelSnapshotStatusSchema>;

// 'discord_channel' rows are the original per-channel chat snapshots (migration
// 0073). 'non_discord_aspects' rows (migration 0102) are per-game theme-
// formation passes that cluster orphan form/playtest aspects.
export const AiChannelSnapshotSourceKindSchema = z.enum([
  'discord_channel',
  'non_discord_aspects',
]);
export type AiChannelSnapshotSourceKind = z.infer<typeof AiChannelSnapshotSourceKindSchema>;

// --- NOTIFY payload ---------------------------------------------------------

// Matches the JSON emitted by migration 0073's trigger, widened in 0102 with
// sourceKind. discordChannelId is non-null iff sourceKind='discord_channel'.
export const AiChannelSnapshotQueuedPayloadSchema = z.object({
  snapshotId: z.string().uuid(),
  studioId: z.string().uuid(),
  gameId: z.string().uuid(),
  sourceKind: AiChannelSnapshotSourceKindSchema.default('discord_channel'),
  discordChannelId: z.string().min(1).nullable(),
});
export type AiChannelSnapshotQueuedPayload = z.infer<typeof AiChannelSnapshotQueuedPayloadSchema>;

// --- Row schema -------------------------------------------------------------

// Camel-case row schema covering every column in the ai_channel_snapshot table.
// Worker-produced fields (populated on markDone) are nullable until the row is done.
// The embedding column is absent from this table — pgvector lives on aspects only.
export const AiChannelSnapshotRowSchema = z.object({
  id: z.string().uuid(),
  studioId: z.string().uuid(),
  gameId: z.string().uuid(),
  sourceKind: AiChannelSnapshotSourceKindSchema.default('discord_channel'),
  discordChannelId: z.string().nullable(),
  windowStart: z.string().datetime({ offset: true }).nullable(),
  windowEnd: z.string().datetime({ offset: true }).nullable(),
  lastMessageIdSeen: z.string().nullable(),
  status: AiChannelSnapshotStatusSchema,
  attemptCount: z.number().int().nonnegative(),
  claimedAt: z.string().datetime({ offset: true }).nullable(),
  nextAttemptAt: z.string().datetime({ offset: true }),
  lastError: z.string().nullable(),
  doneAt: z.string().datetime({ offset: true }).nullable(),
  summary: z.string().nullable(),
  messageCount: z.number().int().nullable(),
  aspectCount: z.number().int().nullable(),
  attachedCount: z.number().int().nullable(),
  newThemeCount: z.number().int().nullable(),
  model: z.string().nullable(),
  promptVersion: z.string().nullable(),
  modelRunId: z.string().nullable(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  costUsd: z.number().nullable(),
  latencyMs: z.number().int().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type AiChannelSnapshotRow = z.infer<typeof AiChannelSnapshotRowSchema>;
